// Client "My APIs" screen — the APIs this user is enabled for IN THE SELECTED
// ENVIRONMENT, plus that environment's credit balance. Env-scoped: re-fetches from
// /console/me/apis whenever the Preprod/Prod toggle changes (the X-Env header is
// attached by the consoleApi interceptor).
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { App, Card, Table, Tag, Typography, Button, Empty, Alert, Statistic, Space } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { meApi } from "../../console/consoleApi";
import { useEnv } from "../../console/useEnv";
import { errorMessage } from "../../console/consoleUtils";
import axiosInstance from "../../utils/axiosConfig";
import { parsePostmanCollection, flattenEndpoints } from "../../utils/apiCollection";

const { Title, Paragraph, Text } = Typography;

const ENV_LABEL = { preprod: "Preprod", prod: "Prod" };

// Normalise a server path for matching (drop trailing slash, lowercase) so a DB
// server_api_url lines up with a docs endpoint path regardless of case/slash.
const normPath = (p) => (p || "").replace(/\/+$/, "").toLowerCase();

export default function ConsoleMyApis() {
  const { message } = App.useApp();
  const { env } = useEnv();
  const navigate = useNavigate();
  const [data, setData] = useState({
    apis: [],
    credits: null,
    provisioned: false,
    unavailable: false,
    allApis: false,
  });
  const [loading, setLoading] = useState(false);
  // path(normalised) -> docs endpoint id, so each API row can deep-link into the docs.
  const [docMap, setDocMap] = useState({});

  // Load the same Postman collection the API-docs page uses and index it by path.
  // Best-effort: if it fails, rows simply render as plain text (no links).
  useEffect(() => {
    let alive = true;
    axiosInstance
      .get("/api/apiCollection/docs", { timeout: 10000 })
      .then((res) => {
        if (!alive) return;
        const byPath = {};
        flattenEndpoints(parsePostmanCollection(res.data)).forEach((e) => {
          if (e.path) byPath[normPath(e.path)] = e.id;
        });
        setDocMap(byPath);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await meApi.apis(env);
      setData(res.data.data);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
    // `env` is a real dep: switching the toggle re-fetches from the other data plane.
  }, [message, env]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    {
      title: "API",
      dataIndex: "name",
      key: "name",
      render: (name, row) =>
        row.docId ? (
          <Button
            type="link"
            style={{ padding: 0, height: "auto" }}
            onClick={() => navigate(`/console/docs?e=${encodeURIComponent(row.docId)}`)}
          >
            {name}
          </Button>
        ) : (
          name
        ),
    },
    {
      title: "",
      key: "status",
      align: "right",
      render: () => <Tag color="green">Enabled</Tag>,
    },
  ];
  // /me/apis returns [{ name, path }] (older shape was a bare string — tolerate both).
  const rows = (data.apis ?? []).map((a) => {
    const name = typeof a === "string" ? a : a.name;
    const path = typeof a === "string" ? null : a.path;
    return { key: name, name, docId: path ? docMap[normPath(path)] : undefined };
  });
  const envLabel = ENV_LABEL[env] ?? env;

  return (
    <Card
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            My APIs
          </Title>
          <Tag color={env === "prod" ? "volcano" : "blue"}>{envLabel}</Tag>
        </Space>
      }
      extra={
        <Button icon={<ReloadOutlined />} onClick={load}>
          Refresh
        </Button>
      }
    >
      <Paragraph type="secondary">
        The APIs you're enabled to call in <Text strong>{envLabel}</Text>. Click an
        API to open its documentation. Switch environment with the toggle in the header.
      </Paragraph>

      {data.unavailable && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={`${envLabel} data isn't available yet`}
          description="This environment isn't set up for the Console yet. Nothing is wrong with your account."
        />
      )}

      {!data.unavailable && !data.provisioned && !loading && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={`You're not provisioned in ${envLabel}`}
          description="Your account doesn't exist in this environment yet. Contact your admin to get access."
        />
      )}

      {data.provisioned && data.allApis && (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          title="Full access — all APIs"
          description={`You're enabled for every API in ${envLabel}, including any added later.`}
        />
      )}

      {data.provisioned && data.credits != null && (
        <Statistic
          title={`${envLabel} credits`}
          value={data.credits}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        rowKey="key"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        locale={{
          emptyText: <Empty description={`No APIs enabled in ${envLabel}.`} />,
        }}
      />
    </Card>
  );
}
